import * as React from "react"
import { ComponentProps, ReactNode } from "react"
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl"
import OverlayTrigger from "@rio-cloud/rio-uikit/OverlayTrigger"
import SimpleButtonDropdown from "@rio-cloud/rio-uikit/SimpleButtonDropdown"
import Tooltip from "@rio-cloud/rio-uikit/Tooltip"
import {
    CurrentTransportLegParty, PartyExternalIdentifiers,
    Shipment,
    ShipmentPriority,
    ShipmentStatus,
    TransportOrder,
    TransportOrderType
} from "@/model/transportorder/transportOrder"
import {
    canTransportOrderBeAccepted,
    isAnyShipmentConfirmed,
    isTransportOrderPartiallyConfirmed,
    isTransportOrderCancelled,
} from "@/model/transportorder/transportOrderEditing"
import {
    AggregationHint,
    hasContributions,
    MeasurementWithAggregationHint,
    TransportOrderMeasurementSummary
} from "@/model/transportorder/transportOrderMeasurements"
import { hasDangerousGoods } from "@/model/transportorder/transportOrderDangerousGoods"
import ActionButton from "../Application/ActionButton"
import { getTimezoneOfCountry } from "@/timezones/timezones"
import moment from "moment/moment"
import { useFeatureToggle } from "@/configuration/featuretoggle/hooks"
import { OverflowClipboardCell } from "./TransportOrderTable/OverflowClipboardCell"
import { isEqualIgnoringUndefined } from "@/helper/equalityHelper"
import Rioglyph from "@rio-cloud/rio-uikit/Rioglyph"

const TEST_ID_DETAILS_DROPDOWN = "DETAILS_DROPDOWN"

export const TimeWindowComponent = (props: {
    from?: string,
    countryCodeFrom?: string,
    to?: string,
    countryCodeTo?: string
}): React.ReactElement => {
    const { from, countryCodeFrom, to, countryCodeTo } = props
    const intl = useIntl()
    const fromTz = getTimezoneOfCountry(countryCodeFrom)
    const toTz = getTimezoneOfCountry(countryCodeTo)
    const dateFormattingProps: Omit<ComponentProps<typeof FormattedDate>, "value" | "timeZone"> =
        { year: "numeric", month: "numeric", day: "2-digit" }
    const timeFormattingProps: Omit<ComponentProps<typeof FormattedDate>, "value" | "timeZone"> =
        { hour: "numeric", minute: "numeric" }
    const fromDate = from ? intl.formatDate(from, { timeZone: fromTz, ...dateFormattingProps }) : undefined
    const toDate = to ? intl.formatDate(to, { timeZone: toTz, ...dateFormattingProps }) : undefined
    const showToDate = fromDate !== toDate
    const showChevron = Boolean(from && to)

    return (
        <div className="width-100pct text-left">
            <div className="display-inline-flex flex-column align-items-center min-width-0">
                {from && (
                    <span className="display-inline-flex flex-column align-items-center min-width-max-content text-center line-height-125rel">
                        <span>
                            <FormattedDate value={from} timeZone={fromTz} {...dateFormattingProps}/>
                        </span>
                        <span>
                            <FormattedDate value={from} timeZone={fromTz} {...timeFormattingProps}/>
                        </span>
                    </span>
                )}
                {showChevron && (
                    <div className="display-flex justify-content-center align-items-center padding-y-5">
                        <span
                            data-testid="time-window-chevron-down"
                            className="rioglyph rioglyph-chevron-down text-size-14 text-color-dark"
                            aria-hidden="true"
                        />
                    </div>
                )}
                {to && (
                    <span className="display-inline-flex flex-column align-items-center min-width-max-content text-center line-height-125rel">
                        {showToDate && (
                            <span>
                                <FormattedDate value={to} timeZone={toTz} {...dateFormattingProps}/>
                            </span>
                        )}
                        <span>
                            <FormattedDate value={to} timeZone={toTz} {...timeFormattingProps}/>
                        </span>
                    </span>
                )}
            </div>
        </div>
    )
}

export const AddressLines = (props: {
    title: ReactNode
    party?: CurrentTransportLegParty,
    partyExternalIdentifiers: PartyExternalIdentifiers
    measureOverflow?: boolean
    cellId: string
}): React.ReactElement => {
    const { party, partyExternalIdentifiers } = props
    if (!party || !party.address) {
        return <React.Fragment/>
    }

    const { name, address } = party
    const { street, city, postal_code, country } = address
    const { duns, plantCode, supplierCode } = partyExternalIdentifiers
    const cityLine = [postal_code, city, country].filter(isDefinedAndNotEmpty).join(" ")
    const copyValue = [name, street, cityLine].filter(isDefinedAndNotEmpty).join("\n")
    const overflowProps = props.measureOverflow ? { "data-overflow-copy-line": "true" } : {}

    return (
        <OverflowClipboardCell cellId={props.cellId} title={props.title} value={copyValue}>
            <div className="display-flex flex-column ellipsis-1 width-100pct" style={{ minWidth: 0 }}>
                {name && (
                    <span className="text-bold ellipsis-1 width-100pct margin-bottom-5" {...overflowProps}>
                        {name}
                    </span>
                )}
                {plantCode ? (
                    <span className='ellipsis-1 text-color-dark'>
                        <Rioglyph
                            icon={"rioglyph rioglyph-factory"}
                            iconClassName={"margin-right-5"}
                        />
                        {plantCode}
                    </span>
                ) : (
                    <span>&nbsp;</span>
                )}
                {street && <span className="ellipsis-1 width-100pct" {...overflowProps}>{street}</span>}
                {cityLine && <span className="ellipsis-1 width-100pct" {...overflowProps}>{cityLine}</span>}
                <PartyIdentifiers duns={duns} supplierCode={supplierCode} />
            </div>
        </OverflowClipboardCell>
    )
}

export const MoreAddressesLabel = (props: {
    shipments: Shipment[],
    extractParty: (item: Shipment) => CurrentTransportLegParty
}): React.ReactNode => {
    const distinctAddresses: CurrentTransportLegParty[] = []
    props.shipments
        .map(props.extractParty)
        .forEach(it => {
            const found = distinctAddresses.find(element =>
                element.name === it.name && isEqualIgnoringUndefined(element.address, it.address))
            if (found == undefined) {
                distinctAddresses.push(it)
            }
        })

    const numDistinctAddresses = distinctAddresses.length
    if (numDistinctAddresses <= 1) {
        return null
    }

    const tooltip = (
        <Tooltip className="width-auto">
            <FormattedMessage
                id={"transportOrder.overview.moreThanOneLoadingAddresses"}
                values={{ numFurtherAddresses: numDistinctAddresses - 1 }}
            />
        </Tooltip>)

    return <div className="padding-left-5">
        <OverlayTrigger placement="right" overlay={tooltip}>
            <span className="badge bg-info">+{numDistinctAddresses - 1}</span>
        </OverlayTrigger>
    </div>
}

export const TransportOrderNumbersCell = (props: { transportOrder: TransportOrder }): ReactNode => {
    const shipmentsWithTransportOrderNumber = (props.transportOrder._embedded?.shipments ?? [])
        .filter(shipment => shipment.external_identification !== undefined)
    const transportOrderNumbers = shipmentsWithTransportOrderNumber
        .map(shipment => shipment.external_identification)
        .join(", ")
    const transportOrderNumberList = (
        <>
            {shipmentsWithTransportOrderNumber.map((shipment, index) => (
                <span key={shipment.id}>
                    <span className={shipment.status === ShipmentStatus.CANCELLED ? "text-decoration-line-through" : ""}>
                        {shipment.external_identification}
                    </span>
                    {index < shipmentsWithTransportOrderNumber.length - 1 ? ", " : ""}
                </span>
            ))}
        </>
    )

    return (
        <OverflowClipboardCell
            cellId={`transport-order-numbers-${props.transportOrder.id}`}
            title={<FormattedMessage id={"transportOrder.transportOrderNumbers"}/>}
            value={transportOrderNumbers}
            popoverContent={transportOrderNumberList}
        >
            <div className="display-flex flex-row no-wrap align-items-center width-100pct" style={{ minWidth: 0 }}>
                <span
                    className="rioglyph rioglyph-delivery margin-right-5"
                    data-testid={`transport-order-numbers-${props.transportOrder.id}-icon`}
                    aria-hidden="true"
                />
                <span
                    className="text-normal ellipsis-1 flex-1-1"
                    style={{ minWidth: 0 }}
                    title={transportOrderNumbers || undefined}
                    data-testid={`transport-order-numbers-${props.transportOrder.id}-value`}
                    data-overflow-copy-line="true"
                >
                    {transportOrderNumberList}
                </span>
            </div>
        </OverflowClipboardCell>
    )
}

export const TransportAndCargoType = (props: {
    transportOrder: TransportOrder
    shipments: Shipment[]
}): React.ReactElement => {
    const hasUrgentShipment = props.shipments.some(shipment => shipment.priority === ShipmentPriority.URGENT)
    const transportTypes: ReactNode[] = props.shipments
        .filter(shipment => shipment.priority !== ShipmentPriority.URGENT)
        .map(shipment => shipment.processIndicator)
        .filter(onlyUniqueFilter)

    if (hasUrgentShipment) {
        transportTypes.push(<FormattedMessage key="urgent" id="transportOrder.transportType.urgent"/>)
    }

    if (transportTypes.length === 0) {
        return <></>
    }

    const cargoTypeMessageId = props.transportOrder.orderType === TransportOrderType.EMPTIES
        ? "shipment.type.empties"
        : "shipment.type.materials"

    return <span>{joinWithComma(transportTypes)} / <FormattedMessage id={cargoTypeMessageId}/></span>
}

const joinWithComma = (nodes: ReactNode[]): ReactNode => {
    if (nodes.length === 0) {
        return <></>
    }
    return nodes.reduce((prev: ReactNode, cur: ReactNode,) => [prev, ", ", cur])
}

const isDefinedAndNotEmpty = (value: string | undefined): value is string => Boolean(value && value.trim() !== "")

const onlyUniqueFilter = (value: string, index: number, self: Array<string>): boolean => self.indexOf(value) === index

export const WeightsByFreightType = (props: { summary: TransportOrderMeasurementSummary }): React.ReactElement => {
    const summary = props.summary
    return <div className="display-flex flex-wrap flex-column align-items-end align-content-center">
        <Measurement display={hasContributions(summary.CARGO)} measurement={summary.CARGO.weight}
            unitTranslationKey={"transportOrder.shipment.cargo.weight.unit.kgm"}/>
        <Measurement display={hasContributions(summary.BULK_DRY)} measurement={summary.BULK_DRY.weight}
            unitTranslationKey={"transportOrder.shipment.cargo.weight.unit.kgm"}/>
        <Measurement display={hasContributions(summary.BULK_LIQUID)} measurement={summary.BULK_LIQUID.weight}
            unitTranslationKey={"transportOrder.shipment.cargo.weight.unit.kgm"}/>
    </div>
}

const MissingDataHint = (): React.ReactElement | null => {

    const intl = useIntl()

    const tooltip = <Tooltip>
        {intl.formatMessage({ id: "transportOrder.overview.freight.aggregate.warning.incompleteData" })}
    </Tooltip>

    return <OverlayTrigger overlay={tooltip} placement={"right"}>
        <span className={"rioglyph rioglyph-warning-sign"} data-testid={"missing-data-hint"}/>
    </OverlayTrigger>
}

function Measurement<T>(props: { display: boolean, measurement: MeasurementWithAggregationHint<T>, unitTranslationKey: string | undefined }): React.ReactElement | null {
    if (!props.display) {
        return null
    }
    if (props.measurement.value === undefined) {
        return <span>-</span>
    }
    let unitDisplay: React.ReactElement | null = null
    if (props.unitTranslationKey) {
        unitDisplay = <React.Fragment>
            &nbsp;
            <FormattedMessage id={props.unitTranslationKey}/>
        </React.Fragment>
    }
    return <span>
        <FormattedNumber value={props.measurement.value} maximumFractionDigits={3}/>
        {unitDisplay}
        {props.measurement.aggregationHint === AggregationHint.MISSING_DATA ? <span>&nbsp;<MissingDataHint/></span> : null}
    </span>
}

export const VolumesByFreightType = (props: { summary: TransportOrderMeasurementSummary }): React.ReactElement => {
    const summary = props.summary
    return <div className="display-flex flex-wrap flex-column align-items-end align-content-center">
        <Measurement display={hasContributions(summary.CARGO)} measurement={summary.CARGO.volume}
            unitTranslationKey={"transportOrder.shipment.cargo.volume.unit.mtq"}/>
        <Measurement display={hasContributions(summary.BULK_DRY)} measurement={summary.BULK_DRY.volume}
            unitTranslationKey={"transportOrder.shipment.cargo.volume.unit.mtq"}/>
        <Measurement display={hasContributions(summary.BULK_LIQUID)} measurement={summary.BULK_LIQUID.volume}
            unitTranslationKey={"transportOrder.shipment.cargo.volume.unit.mtq"}/>
    </div>
}

export const HandlingUnitCountByFreightType = (props: { summary: TransportOrderMeasurementSummary }): React.ReactElement => {
    const summary = props.summary
    return <div className="display-flex flex-wrap flex-column align-items-end align-content-center">
        <Measurement display={hasContributions(summary.CARGO)} measurement={summary.CARGO.count}
            unitTranslationKey={undefined}/>
        <Measurement display={hasContributions(summary.BULK_DRY)} measurement={summary.BULK_DRY.count}
            unitTranslationKey={undefined}/>
        <Measurement display={hasContributions(summary.BULK_LIQUID)} measurement={summary.BULK_LIQUID.count}
            unitTranslationKey={undefined}/>
    </div>
}

interface TransportOrderActionsDropdownProps {
    transportOrder: TransportOrder
    onDetailsClicked: (transportOrder: TransportOrder) => void
    onAcceptClicked: (transportOrder: TransportOrder) => void
    onFreightPaymentDetailsClicked: (transportOrder: TransportOrder) => void
}

const canTransportOrderBeAcceptedFromOverview = (transportOrder: TransportOrder): boolean => {
    return canTransportOrderBeAccepted(transportOrder)
        && !isAnyShipmentConfirmed(transportOrder)
}

export const TransportOrderActionsDropdown = (props: TransportOrderActionsDropdownProps): React.ReactElement => {
    const iconAndText = (cssClass: string, messageId: string, attributes: Record<string, string>): ReactNode =>
        <div {...attributes} className={"display-flex gap-5"}>
            <span className={`text-size-20 rioglyph ${cssClass}`}/>
            <span className={"padding-top-2"}><FormattedMessage id={messageId}/></span>
        </div>

    const transportOrder = props.transportOrder

    const items: { value: ReactNode, onSelect?: () => void, disabled?: boolean }[] = [
        {
            value: iconAndText("rioglyph-detail-view-info", "transportOrder.overview.menu.details", {
                "data-testid": "details-menu-entry",
                "data-track-ga-event-trigger": "click",
                "data-track-ga-event-category": "dispatch, transportOrder, transportOrderOverview",
                "data-track-ga-event-action": "detailsButton",
            }),
            onSelect: () => props.onDetailsClicked(transportOrder)
        },
    ]
    if (canTransportOrderBeAcceptedFromOverview(transportOrder)) {
        items.push({
            value: iconAndText("rioglyph-thumbs-up", "transportOrder.accept", {
                "data-track-ga-event-trigger": "click",
                "data-track-ga-event-category": "dispatch, transportOrder, transportOrderOverview",
                "data-track-ga-event-action": "acceptButton",
            }),
            onSelect: () => props.onAcceptClicked(transportOrder),
        })
    }

    const showTransportationReportFeatureToggle = useFeatureToggle("showTransportationReport", false)

    const showFreightPaymentDetails = (transportOrder: TransportOrder): boolean => {
        if (!showTransportationReportFeatureToggle) {
            return false
        }

        if (isTransportOrderCancelled(transportOrder)) {
            return false
        }
        const nonCancelled = transportOrder._embedded?.shipments
            .filter(shipment => shipment.status !== ShipmentStatus.CANCELLED)
        if (nonCancelled === undefined || nonCancelled.length === 0) {
            return false
        }
        const referenceTimestamps = nonCancelled
            .map(order =>
                order.confirmationDraft.unloadingTimeWindow?.time_to
                ?? order.currentTransportLeg.arrivalEstimatedAt?.time_to
                ?? order.endOfTransportChain.deliveryEstimatedAt?.time_to
                ?? order.startOfTransportChain.pickupRequestedAt.time_to
            )
            .map(it => moment(it))
        if (referenceTimestamps.length === 0) {
            return false
        }
        const referenceTimestamp = moment.max(referenceTimestamps)
        return moment().isAfter(referenceTimestamp)
    }

    if (showFreightPaymentDetails(transportOrder)) {
        items.push({
            value: iconAndText("rioglyph rioglyph-document", "freightPaymentDetails.button.label", {}),
            onSelect: () => props.onFreightPaymentDetailsClicked(transportOrder),
        })
    }

    return(
        <div data-testid={TEST_ID_DETAILS_DROPDOWN}>
            <SimpleButtonDropdown
                bsSize={"sm"}
                items={items}
                title={<span className="rioglyph rioglyph-option-vertical cursor-pointer text-color-dark hover-text-color-highlight"/>}
                iconOnly={true}
                variant={"link"}
            />
        </div>
    )
}

export const DangerousGoodsIndicator = (props: { shipments: Shipment[] }): React.ReactElement => {
    if (hasDangerousGoods(props.shipments)) {
        return <span className={"rioglyph rioglyph-ok text-size-18"} data-testid={"dangerous-goods-indicator"}/>
    }
    return <React.Fragment/>
}

export const PartialConfirmationWarning = (props: { transportOrder: TransportOrder }): React.ReactElement => {
    const intl = useIntl()
    const nonCancelledShipments = props.transportOrder._embedded?.shipments.filter(
        shipment => shipment.status !== ShipmentStatus.CANCELLED
    ) ?? []
    const confirmedCount = nonCancelledShipments.filter(shipment => shipment.isConfirmed).length
    const label = intl.formatMessage(
        { id: "transportOrder.partiallyAccepted.tooltip" },
        { confirmedCount, totalCount: nonCancelledShipments.length }
    )

    if (!isTransportOrderPartiallyConfirmed(props.transportOrder)) {
        return <React.Fragment/>
    }

    return <OverlayTrigger
        placement="right"
        overlay={<Tooltip id="partially-confirmed-tooltip" width={300} textAlignment="left">{label}</Tooltip>}
    >
        <span
            aria-label={label}
            className="rioglyph rioglyph-warning-sign text-color-warning text-size-h5"
            data-testid="partially-confirmed-icon"
        />
    </OverlayTrigger>
}

export const TransportOrderStatusOrAcceptBadge = (props: { transportOrder: TransportOrder, onAcceptClicked: (transportOrder: TransportOrder) => Promise<void> }): React.ReactElement => {
    const transportOrder = props.transportOrder

    if (isTransportOrderCancelled(transportOrder)) {
        return (<span className="text-color-danger" data-testid={"cancelled-badge"}>
            <FormattedMessage id="transportOrder.cancelled"/>
        </span>)
    }
    if (!canTransportOrderBeAccepted((transportOrder))) {
        return <React.Fragment/>
    }
    if (isTransportOrderPartiallyConfirmed(transportOrder)) {
        return <span className="text-color-warning" data-testid="partially-accepted-status">
            <FormattedMessage id="transportOrder.partiallyAccepted"/>
        </span>
    }
    if (isAnyShipmentConfirmed(transportOrder)) {
        return <span className="text-color-highlight" data-testid={"accepted-badge"}>
            <FormattedMessage id="transportOrder.accepted"/>
        </span>
    }

    if (canTransportOrderBeAcceptedFromOverview(transportOrder)) {
        return <ActionButton
            data-track-ga-event-trigger="click"
            data-track-ga-event-category="dispatch, transportOrder, transportOrderOverview"
            data-track-ga-event-action="acceptButton"
            btnClass="btn btn-primary"
            onClick={(): Promise<void> => props.onAcceptClicked(transportOrder)}
            disableSuccessDifferentiation={true}
            dataTestId={"accept-button"}>
            <FormattedMessage id="transportOrder.accept"/>
        </ActionButton>
    }

    return <React.Fragment/>
}

type PartyIdentifiersProps = {
    readonly duns?: string;
    readonly supplierCode?: string;
};

export const PartyIdentifiers = ({ duns, supplierCode }: PartyIdentifiersProps) => (
    <div className='margin-top-5'>
        {duns && <div className='ellipsis-1 text-color-dark text-size-12'>
            DUNS: {duns}
        </div>}
        {supplierCode && <div className='ellipsis-1 text-color-dark text-size-12'>
            Partner-ID: {supplierCode}
        </div>}
    </div>
)
